import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { message } from 'antd';
import { getFormGroups } from '../services/addFormApi';
import { getRecordForEdit, updateRecord } from '../services/detailedViewApi';

export function useEditForm({
    moduleName,
    recordId,
    cancelPath,
    onSuccess,
    fieldNameMap = {},
    onFileUpload = null,
}) {
    const navigate = useNavigate();

    const [formGroups, setFormGroups] = useState([]);
    const [rawRecord, setRawRecord] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    // Fetch form config + record
    useEffect(() => {
        if (!moduleName || !recordId) return;
        let cancelled = false;

        async function fetchData() {
            try {
                setLoading(true);
                setError('');

                const [groupsRes, recordRes] = await Promise.all([
                    getFormGroups({ module: moduleName, id: recordId }),
                    getRecordForEdit(moduleName, recordId),
                ]);

                if (cancelled) return;
                setFormGroups(Array.isArray(groupsRes) ? groupsRes : []);
                setRawRecord(recordRes ?? {});
            } catch (err) {
                if (!cancelled) setError(err?.message || 'Failed to load record');
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        fetchData();
        return () => { cancelled = true; };
    }, [moduleName, recordId]);

    // Build payload — generic, works for all modules
    function buildPayload(values, groups) {
        const payload = { ...rawRecord };

        const allFields = groups.flatMap(g => g.fields ?? []);

        Object.entries(values).forEach(([key, value]) => {
            if (value === undefined) return;

            const field = allFields.find(f => f.field === key);
            if (field?.type === 'file') return; // uploaded separately

            if (dayjs.isDayjs(value)) {
                applyDotValue(payload, key, value.format('MM-DD-YYYY'));
                return;
            }

            applyDotValue(payload, key, value);
        });

        // Clean leftover dot-notation keys
        Object.keys(payload).forEach(k => {
            if (k.includes('.')) delete payload[k];
        });

        // Apply field name remapping (e.g. mspReqId → mspreqId)
        Object.entries(fieldNameMap).forEach(([from, to]) => {
            if (payload[from] !== undefined) {
                payload[to] = payload[from];
                delete payload[from];
            }
        });

        // Strip non-ISO date strings Go can't parse
        Object.keys(payload).forEach(key => {
            const val = payload[key];
            if (typeof val === 'string' && val !== '') {
                const isDateKey = /(date|time|Date|Time|At|at)$/.test(key);
                const isRelative = /ago|month|year|day|week|hour|minute/i.test(val);
                const isISO = /^\d{4}-\d{2}-\d{2}/.test(val);
                if (isDateKey && (isRelative || !isISO)) delete payload[key];
            }
        });

        // Strip raw file objects
        Object.keys(payload).forEach(key => {
            const val = payload[key];
            if (Array.isArray(val) && val[0]?.originFileObj) delete payload[key];
        });

        return payload;
    }

    // Submit
    async function submit(values, groups) {
        try {
            setSubmitting(true);
            setSubmitError('');

            // Upload files first
            if (onFileUpload) {
                const fileFields = groups
                    .flatMap(g => g.fields ?? [])
                    .filter(f => f.type === 'file');

                for (const field of fileFields) {
                    const fileList = values[field.field];
                    if (!Array.isArray(fileList)) continue;
                    const newFiles = fileList.filter(f => f.originFileObj instanceof File);
                    await Promise.all(
                        newFiles.map(f => onFileUpload(field.field, f.originFileObj, recordId))
                    );
                }
            }

            await updateRecord(moduleName, recordId, buildPayload(values, groups));
            message.success(`${moduleName} updated successfully`);

            if (onSuccess) onSuccess();
            else if (cancelPath) navigate(cancelPath);
            else navigate(-1);

        } catch (err) {
            setSubmitError(err?.message || 'Failed to save changes');
            message.error(err?.message || 'Failed to save changes');
        } finally {
            setSubmitting(false);
        }
    }

    return {
        formGroups,
        rawRecord,
        loading,
        error,
        submitting,
        submitError,
        submit,
    };
}